import React, { useRef, useState } from 'react';
import { DateRangePicker } from 'react-date-plus-time-range';
import Popper from '@material-ui/core/Popper';
import moment from 'moment/moment';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import TextBox from 'UI/components/atoms/TextBox';
import FooterActionsControlsLegacy from 'UI/components/molecules/FooterActionsControlsLegacy';
import { DateFormats } from 'UI/constants/defaults';

import { useStyles } from './DateRange.styled';

import 'UI/res/dateRangePicker.css';

const datePickerKey = 'selection';

/**
 * @param {Object} props
 * @param {*} [props.DateRangerPickerProps]
 * @param {DatePeriod} [props.defaultRange]
 * @param {boolean} props.disableTimeRange
 * @param {boolean} [props.isPeriodClearable]
 * @param {Function} props.onPeriodChange
 */
export const DateRange = ({
  DateRangerPickerProps,
  defaultRange,
  disableTimeRange,
  isPeriodClearable,
  monthsToShow = 2,
  onPeriodChange
}) => {
  const classes = useStyles();
  const dateFormatToShow = disableTimeRange ? DateFormats.SimpleDate : DateFormats.SimpleDateTime;

  const anchorRef = useRef(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState(defaultRange);
  const [previewRange, setPreviewRange] = useState([
    {
      ...defaultRange,
      key: datePickerKey
    }
  ]);

  const shouldShowPeriodClearButton = isPeriodClearable || !defaultRange;

  const toggleDatePicker = val => () => {
    setIsDatePickerOpen(val);
  };

  const handleDateRangeChange = item => {
    const { selection } = item || {};

    const isStartDateValid = moment(selection.startDate).isValid();
    const isEndDateValid = moment(selection.endDate).isValid();

    if (!isStartDateValid || !isEndDateValid) return;

    const endHours = selection.endDate.getHours();
    const endMinutes = selection.endDate.getMinutes();
    const timeStartsAtZero = endHours === 0 && endMinutes === 0;

    setPreviewRange([
      {
        ...selection,
        ...(timeStartsAtZero ? { endDate: moment(selection.endDate).endOf('day').toDate() } : {})
      }
    ]);
  };

  const handleDateRangeCancel = () => {
    setIsDatePickerOpen(false);
    selectedPeriod &&
      setPreviewRange([
        {
          key: datePickerKey,
          startDate: selectedPeriod.startDate,
          endDate: selectedPeriod.endDate
        }
      ]);
  };

  const handleDateRangeApply = () => {
    const { startDate, endDate } = previewRange[0];
    setIsDatePickerOpen(false);
    const newPeriod = {
      startDate,
      endDate
    };
    setSelectedPeriod(newPeriod);
    onPeriodChange && onPeriodChange(newPeriod);
  };

  const handleClearPeriod = () => {
    setIsDatePickerOpen(false);
    setSelectedPeriod(null);
    setPreviewRange([{ key: datePickerKey }]);
    onPeriodChange && onPeriodChange(null);
  };

  return (
    <div ref={anchorRef}>
      <TextBox
        name="dateRange"
        label="Date Range"
        onFocus={toggleDatePicker(true)}
        minWidth={370}
        width={370}
        value={
          selectedPeriod
            ? `${moment(selectedPeriod.startDate).format(dateFormatToShow)} - ${moment(
                selectedPeriod.endDate
              ).format(dateFormatToShow)}`
            : 'All Time'
        }
      />
      <Popper
        open={isDatePickerOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className={classes.popper}
        modifiers={{
          flip: { enabled: true },
          preventOverflow: { enabled: true, boundariesElement: 'viewport' }
        }}
      >
        <div className={classes.dateRangeContainer}>
          <DateRangePicker
            onChange={handleDateRangeChange}
            showSelectionPreview
            inputRanges={[]}
            months={monthsToShow}
            ranges={previewRange}
            showTime={!disableTimeRange}
            direction="horizontal"
            {...DateRangerPickerProps}
          />
          <div className={classes.dateRangeToolbar}>
            <FooterActionsControlsLegacy
              secondaryAction={handleDateRangeCancel}
              saveAction={false}
              secondaryProps={{ size: 'small' }}
              primaryProps={{
                onClick: handleDateRangeApply,
                text: 'Apply filters',
                size: 'small'
              }}
              customThirdButton={
                shouldShowPeriodClearButton && (
                  <FPActionButton
                    text="Clear"
                    variant="outlined"
                    onClick={handleClearPeriod}
                    size="small"
                  />
                )
              }
            />
          </div>
        </div>
      </Popper>
    </div>
  );
};
